import { z } from "zod";

import ExamWithCertification from "@/services/catalogService/ExamWithCertification";
import http from "@/services/http";

export const CourseIdPath = z.object({
  id: z.number(),
  type: z.string(),
  guid: z.string(),
  title: z.string(),
  paths: z.array(
    z.object({
      id: z.number(),
      guid: z.string(),
      type: z.string(),
      name: z.string(),
      acronym: z.string().nullish(),
      display_version: z.string().nullish(),
    })
  ),
});

// content-catalog-service's Content struct declares FriendlyUrl as *string (no omitempty), so it
// can genuinely serialize as null - global_products reuses the same shape across services.
const globalProduct = z
  .object({
    content_bundle_id: z.union([z.number(), z.string()]).nullish(),
  })
  .loose();

export const courseDetailResponse = z
  .object({
    guid: z.string(),
    type: z.string(),
    title: z.string(),
    friendly_url: z.string().nullish(),
    global_products: z.array(globalProduct).nullish(),
    active: z.number().nullish(),
    url: z.string().nullish(),
    tracks: z
      .array(z.object({ id: z.number(), guid: z.string(), type: z.string() }).loose())
      .nullish(),
    catalog_data: z.object({ url: z.string().nullish() }).loose().nullish(),
  })
  .loose();

// GET /courses (list/carousel) wraps results in the same {data, metadata, total_records, ...}
// envelope as GET /certifications (both backed by the shared dto.Result struct).
const coursesListResponse = z.object({
  data: z.array(courseDetailResponse),
});
export { coursesListResponse };

// POST /retrieveCatalogItems - heterogeneous catalog records; only `guid` is universal
// (dto.GetCatalogItemsResponse.Data is untyped []map[string]interface{} on the Go side too).
export const catalogItem = z
  .object({
    guid: z.string(),
  })
  .loose();

export const catalogItemsResponse = z.object({
  Data: z.array(catalogItem).nullish(),
});

export async function fetch(Id: string, includeParent?: boolean) {
  const { data } = await http().get(
    `${globalThis.env.API_CATALOG}/courses/${Id}${includeParent ? "?includeParent=true" : ""}`
  );
  return courseDetailResponse.parse(data);
}

export async function carousel(type: string) {
  const { data } = await http().get(`${globalThis.env.API_CATALOG}/courses`, {
    params: { type, length: 18 },
  });

  return coursesListResponse.parse(data).data;
}

export async function retrieveCatalogItems(guids: Array<string>, type?: string) {
  const { data } = await http().post(`${globalThis.env.API_CATALOG}/retrieveCatalogItems`, {
    guids: guids,
    type: type ?? null,
  });

  return catalogItemsResponse.parse(data).Data ?? [];
}

export async function retrieveCatalogById(ids: Array<number>, type?: string) {
  const { data } = await http().post(`${globalThis.env.API_CATALOG}/retrieveCatalogItems`, {
    ids: ids,
    type: type ?? null,
  });

  return catalogItemsResponse.parse(data).Data ?? [];
}

export async function retrieveAllCatalogItems(
  guids: Array<string>,
  ids: Array<number>,
  type?: string,
  include_all?: boolean
) {
  const { data } = await http().post(`${globalThis.env.API_CATALOG}/retrieveCatalogItems`, {
    guids: guids.length ? guids : null,
    ids: ids.length ? ids : null,
    type: type ?? null,
    include_all: include_all ?? false,
  });

  return catalogItemsResponse.parse(data).Data ?? [];
}

export async function getCoursePaths(id: number) {
  if (!globalThis.env.API_CATALOG || id < 0) {
    return;
  }
  const { data } = await http().get(`${globalThis.env.API_CATALOG}/courses/${id}/paths`);

  return CourseIdPath.parse(data);
}

export async function getExam(
  examIds: Array<string>,
  includeCertifications?: boolean,
  includeTopics?: boolean
) {
  const { data } = await http().post(`${globalThis.env.API_CERTIFICATION}/exams/search`, {
    exam_number: examIds,
    include_certifications: includeCertifications,
    include_topics: includeTopics,
    include_prescribed_prep_material: true,
  });
  return ExamWithCertification.parse(data.data);
}
